import { useCallback, useState } from "react";
import { useLang } from "../hooks/useLang";
import type { BankInfo } from "../services/api";

// BankTransferPanel — kredi yükleme ekranındaki havale/EFT sekmesi.
//
// Düzen TANITIM SİTESİNDEKİ "Banka Hesapları" sayfasıyla aynıdır: para birimi
// başına bir kart, kartın içinde hesap sahibi / banka / IBAN / SWIFT. Bilgiler
// kart başına TEKRAR EDER — sitede de öyle. Sebep: kullanıcı para birimine göre
// tek bir kart seçip banka ekranına geçiyor; ortak alanları yukarı toplamak
// onu iki yere birden bakmaya zorluyordu.
//
// Panelin siteden farkı: her kart SEÇİLİ ADEDİN o para birimindeki tutarını da
// yazar (sitede adet kavramı yok).
//
// Bilgiler /api/v1/me → bank_accounts ile gelir; kaynağı kök dizindeki
// bank-accounts.json'dur ve site sayfası da AYNI dosyadan üretilir. Panelde
// ayrı bir kopya TUTULMAZ — biri güncellenip diğeri unutulursa müşteri kapanmış
// bir hesaba para gönderir.
//
// DÜRÜSTLÜK: havale kredileri OTOMATİK yüklemez. Kart ve PayPal'da tahsilat
// doğrulanıp kredi anında tanımlanıyor; havalede böyle bir mutabakat yok.
// Metin bunu açıkça söyler — "ödedim, kredim nerede" sorusunun kaynağı budur.

export default function BankTransferPanel({ bank, credits, totalTL, totalUSD, totalEUR }: {
  bank: BankInfo;
  /** Seçili kredi adedi — açıklama satırı önerisinde kullanılır. */
  credits: number;
  totalTL: number;
  totalUSD: number;
  /** EUR karşılığı. Kur tanımsızsa 0 gelir ve EUR kartında tutar yazılmaz. */
  totalEUR: number;
}) {
  const { t } = useLang();
  const [copied, setCopied] = useState<string | null>(null);

  const copy = useCallback(async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Pano izni yoksa/reddedilirse eski yönteme düş — reddi doğrudan hata
      // saymak yaygın bir hata (web/assets/site.js'te de aynı ders alınmıştı).
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* sessiz */ }
      document.body.removeChild(ta);
    }
    setCopied(key);
    window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1800);
  }, []);

  // Görüntülemede 4'lü gruplama okumayı kolaylaştırır; PANOYA giden değer
  // boşluksuz ham IBAN'dır (banka arayüzleri ikisini de kabul eder ama
  // boşluksuz olan her yerde güvenli).
  const pretty = (iban: string) => iban.replace(/(.{4})/g, "$1 ").trim();

  const CUR_NAME: Record<string, string> = {
    TL: t("bt_cur_tl"), USD: t("bt_cur_usd"), EUR: t("bt_cur_eur"),
  };
  // Tutar YALNIZ kuru tanımlıysa yazılır. Kur 0'ken "0.00 EUR" göstermek
  // müşteriyi yanlış tutar göndermeye iter — hiç yazmamak doğrudur.
  const AMOUNT: Record<string, number> = { TL: totalTL, USD: totalUSD, EUR: totalEUR };
  const amountFor = (code: string) => {
    const v = AMOUNT[code];
    return v > 0 ? `${v.toFixed(2)} ${code}` : null;
  };

  return (
    <section className="settings-section bank-transfer">
      <h3>{t("bt_title")}</h3>
      <p className="hint">{t("bt_intro")}</p>

      <div className="bt-grid">
        {bank.accounts.map((a) => {
          const amt = amountFor(a.code);
          return (
            <article className="bt-card" key={a.code}>
              <header className="bt-head">
                <span className="bt-cur">{a.code}</span>
                <h4 className="bt-cur-name">{CUR_NAME[a.code] ?? a.code}</h4>
              </header>

              {/* Seçili adedin bu para birimindeki karşılığı — panelin siteden
                  farkı bu satır. Kur tanımsızsa hiç basılmaz. */}
              {amt && (
                <p className="bt-amount">
                  <span className="bt-amount-label">{t("bt_amount_label")}</span>
                  <strong className="bt-amount-value">{amt}</strong>
                </p>
              )}

              <dl className="bt-dl">
                <dt>{t("bt_holder")}</dt>
                <dd>{bank.holder}</dd>
                <dt>{t("bt_bank")}</dt>
                <dd>{bank.bank}</dd>
                <dt>{t("bt_iban")}</dt>
                <dd className="bt-iban-row">
                  <code className="bt-iban">{pretty(a.iban)}</code>
                  <button
                    type="button"
                    className="btn-sec bt-copy"
                    onClick={() => copy(a.iban, a.code)}
                    aria-label={`${t("bt_copy")} — ${a.code} IBAN`}
                  >
                    {copied === a.code ? t("bt_copied") : t("bt_copy")}
                  </button>
                </dd>
                <dt>{t("bt_swift")}</dt>
                <dd>
                  <code className="bt-code">{bank.swift}</code>
                  <span className="bt-swift-hint">{t("bt_swift_hint").replace("{c}", bank.swift)}</span>
                </dd>
              </dl>
            </article>
          );
        })}
      </div>

      {/* Açıklama alanı önerisi: eşleştirmeyi hızlandıran tek şey bu. */}
      <p className="hint bt-note">
        {t("bt_reference").replace("{n}", String(credits))}
      </p>
      {/* Kredi otomatik yüklenmez — bunu gizlemek destek talebi üretir. */}
      <p className="hint bt-manual">{t("bt_manual")}</p>
      <p className="bt-live" role="status" aria-live="polite">
        {copied ? t("bt_copied") : ""}
      </p>
    </section>
  );
}
